import { Injectable } from '@angular/core';
import { UUID } from '@macs-frontend/core/utils/uuid';
import {
  AggregatedClient,
  APIResponse,
  Client,
  ClientStatuses,
  RatesheetStatuses,
} from '@macs-frontend/web-macs/shared';
import { ApiService } from '../../../../core';
import { ClientSorter } from '../reducers/clients';

@Injectable({
  providedIn: 'root',
})
export class ClientsApiService extends ApiService {
  getClients(
    limit: number,
    status: ClientStatuses,
    sorter: ClientSorter,
    search: string,
    page = 1,
    queryParams = {},
  ) {
    return this.http.get<APIResponse<Array<AggregatedClient>>>(
      `${this.API_PATH}/company_management/manage/clients?limit=${limit}&sort_by(${
        sorter.type
      })=${sorter.order}&status=${status}&search=${search}&page=${page}&${this.stringify(
        queryParams,
      )}`,
    );
  }

  getClientsByBtwVat(btwVat: string, limit = 1, page = 1) {
    return this.http.get(
      `${this.API_PATH}/company_management/manage/clients?btwVat=${btwVat}&limit=${limit}&page=${page}&status=${ClientStatuses.ACTIVE}`,
    );
  }

  getClientsByName(name: string, limit = 1, page = 1) {
    return this.http.get(
      `${this.API_PATH}/company_management/manage/clients?name=${name}&limit=${limit}&page=${page}`,
    );
  }

  getClient(clientUUID: UUID | string, exactSync = false) {
    return this.http.get<APIResponse<Client>>(
      `${
        this.API_PATH
      }/company_management/manage/clients/${clientUUID}?exactSync=${Number(exactSync)}`,
    );
  }

  getClientGroups(limit: number, search = '', page = 1, queryParams = {}) {
    return this.http.get(
      `${
        this.API_PATH
      }/company_management/manage/company_categories?type=CLIENTS&limit=${limit}&search=${search}&page=${page}&${this.stringify(
        queryParams,
      )}`,
    );
  }

  getClientGroup(groupUUID: UUID) {
    return this.http.get(
      `${this.API_PATH}/company_management/manage/company_categories/${groupUUID}`,
    );
  }

  postClient(data: any) {
    return this.http.post(`${this.API_PATH}/company_management/manage/clients`, data);
  }

  putClient(clientId: UUID, data: any) {
    return this.http.put(
      `${this.API_PATH}/company_management/manage/clients/${clientId}`,
      data,
    );
  }

  postClientGroup(data: any) {
    return this.http.post(
      `${this.API_PATH}/company_management/manage/company_categories`,
      { ...data, type: 'CLIENTS' },
    );
  }

  patchClient(clientUUID: UUID, data: any) {
    return this.http.patch<APIResponse<Client>>(
      `${this.API_PATH}/company_management/manage/clients/${clientUUID}`,
      data,
    );
  }

  deleteClient(clientUUID: UUID) {
    return this.http.delete(
      `${this.API_PATH}/company_management/manage/clients/${clientUUID}`,
    );
  }

  validateClientName(name: string) {
    return this.http.head(
      `${this.API_PATH}/company_management/manage/clients/name/${name}`,
    );
  }

  getClientsWithRatesheets(
    status: RatesheetStatuses | undefined,
    search: string,
    page = 1,
    limit: number,
    queryParams = {},
  ) {
    const queryString = this.stringify({
      ...queryParams,
      status,
      search,
      page,
      limit,
    });
    return this.http.get<APIResponse<Client[]>>(
      `${this.API_PATH}/company_management/manage/ratesheets/grouped-by-clients?${queryString}`,
    );
  }
}
